import { useEffect, useMemo, useState } from 'react';
import { ReturnsIntakeFilters } from '../models';
import { useLocation } from 'react-router-dom';
import { useQueryParams } from '@/hooks';

const parseIsoDateFromJson = (
  value: unknown,
  fallback: Date | null | undefined
): Date | null | undefined => {
  if (value == null || value === '') return fallback;
  const parsed = new Date(value as string);
  return Number.isNaN(parsed.getTime()) ? fallback : parsed;
};

const RETURNS_INTAKE_FILTER_QUERY_FIELDS = {
  string: [
    'driverId',
    'commerceId',
    'returnsIntakeStatus',
    'departmentId',
    'shippingNumber',
  ],
  isoDate: [
    'startDate',
    'endDate',
  ],
} satisfies {
  string: readonly (keyof ReturnsIntakeFilters)[];
  isoDate: readonly (keyof ReturnsIntakeFilters)[];
};

const resolveInitialReturnsIntakeFilter = (
  emptyFilterTemplate: ReturnsIntakeFilters,
  locationSearch: string,
  readQueryParam: (key: string) => string | null,
  storageKey?: string
): ReturnsIntakeFilters => {
  const storageJson = storageKey ? localStorage.getItem(storageKey) : null;
  let filterAfterStorage: ReturnsIntakeFilters = emptyFilterTemplate;

  if (storageJson) {
    try {
      const parsed = JSON.parse(storageJson) as Record<string, unknown>;

      filterAfterStorage = {
        ...emptyFilterTemplate,
        driverId:
          typeof parsed.driverId === 'string'
            ? parsed.driverId
            : emptyFilterTemplate.driverId,
        commerceId:
          typeof parsed.commerceId === 'string'
            ? parsed.commerceId
            : emptyFilterTemplate.commerceId,
        returnsIntakeStatus:
          typeof parsed.returnsIntakeStatus === 'string'
            ? parsed.returnsIntakeStatus
            : emptyFilterTemplate.returnsIntakeStatus,
        departmentId:
          typeof parsed.departmentId === 'string'
            ? parsed.departmentId
            : emptyFilterTemplate.departmentId,
        shippingNumber:
          typeof parsed.shippingNumber === 'string'
            ? parsed.shippingNumber
            : emptyFilterTemplate.shippingNumber,
        startDate: parseIsoDateFromJson(
          parsed.startDate,
          emptyFilterTemplate.startDate
        ),
        endDate: parseIsoDateFromJson(
          parsed.endDate,
          emptyFilterTemplate.endDate
        ),
      };
    } catch {
      filterAfterStorage = emptyFilterTemplate;
    }
  }

  const urlParams = new URLSearchParams(locationSearch);
  const filterAfterUrl = { ...filterAfterStorage };

  for (const key of RETURNS_INTAKE_FILTER_QUERY_FIELDS.string) {
    if (!urlParams.has(key)) continue;
    const fromUrl = readQueryParam(key);
    (filterAfterUrl as Record<string, unknown>)[key] = fromUrl ?? '';
  }

  for (const key of RETURNS_INTAKE_FILTER_QUERY_FIELDS.isoDate) {
    if (!urlParams.has(key)) continue;
    const fromUrl = readQueryParam(key);
    if (!fromUrl) {
      (filterAfterUrl as Record<string, unknown>)[key] =
        emptyFilterTemplate[key];
      continue;
    }
    const parsedDate = new Date(fromUrl);
    (filterAfterUrl as Record<string, unknown>)[key] = Number.isNaN(
      parsedDate.getTime()
    )
      ? emptyFilterTemplate[key]
      : parsedDate;
  }

  return filterAfterUrl;
};

const saveReturnsIntakeFilterToLocalStorage = (
  storageKey: string,
  filter: ReturnsIntakeFilters
) => {
  const jsonSafeFilter = {
    driverId: filter.driverId,
    commerceId: filter.commerceId,
    returnsIntakeStatus: filter.returnsIntakeStatus,
    departmentId: filter.departmentId,
    shippingNumber: filter.shippingNumber,
    startDate: filter.startDate?.toISOString() ?? null,
    endDate: filter.endDate?.toISOString() ?? null,
  };
  try {
    localStorage.setItem(storageKey, JSON.stringify(jsonSafeFilter));
  } catch {
  }
};

const buildUrlQueryFromReturnsIntakeFilter = (filter: ReturnsIntakeFilters) => ({
  driverId: filter.driverId || undefined,
  commerceId: filter.commerceId || undefined,
  returnsIntakeStatus: filter.returnsIntakeStatus || undefined,
  departmentId: filter.departmentId || undefined,
  shippingNumber: filter.shippingNumber || undefined,
  startDate: filter.startDate?.toISOString() || undefined,
  endDate: filter.endDate?.toISOString() || undefined,
});

const useReturnsIntakeFilter = (useDefaultDates = false, storageKey?: string) => {
  const location = useLocation();
  const { getQueryParam, setQueryParams } = useQueryParams();

  const initialFilter: ReturnsIntakeFilters = useMemo(() => {
    let startDate = undefined;
    let endDate = undefined;

    if (useDefaultDates) {
      const startOfToday = new Date();
      startOfToday.setHours(0, 0, 0, 0);

      const endOfToday = new Date();
      endOfToday.setHours(23, 59, 59, 999);

      startDate = startOfToday;
      endDate = endOfToday;
    }

    return {
      driverId: '',
      commerceId: '',
      returnsIntakeStatus: undefined,
      departmentId: '',
      shippingNumber: '',
      startDate,
      endDate,
    };
  }, [useDefaultDates]);

  const [filter, setFilter] = useState<ReturnsIntakeFilters>(() =>
    resolveInitialReturnsIntakeFilter(
      initialFilter,
      location.search,
      getQueryParam,
      storageKey
    )
  );

  useEffect(() => {
    if (storageKey) {
      saveReturnsIntakeFilterToLocalStorage(storageKey, filter);
    }
    setQueryParams(buildUrlQueryFromReturnsIntakeFilter(filter));
  }, [filter]);

  return { filter, initialFilter, setFilter };
};

export default useReturnsIntakeFilter;
